import { differenceInCalendarDays } from 'date-fns'
import { formatDate, parseDate } from '@/dates'
import type { HostLookupTableState } from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeResolutionSourceStatus } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * One labelled figure in the card: caption, value, and the note under it.
 *
 * `emphasis` is the row count and only the row count. It is the number the
 * screen exists to answer ("is my market in there"), and the design sizes its
 * text box half again as tall as the provenance tiles beside it, so the
 * contrast is the point rather than decoration.
 *
 * The 26px is a literal because the type scale has no token there: it runs
 * 22px (`text-2xl`) then 24px (`text-display-xs`), and rounding to either
 * would quietly restyle a size the design states outright. Line height comes
 * from the same source.
 */
function StatusTile({
	label,
	value,
	sublabel,
	emphasis
}: {
	label: string
	value: string
	sublabel?: string
	emphasis?: boolean
}) {
	return (
		<div className='flex min-w-0 flex-col gap-1'>
			<span className='text-neutral-500 text-sm'>{label}</span>
			<span
				className={cn(
					'truncate font-semibold text-neutral-900',
					emphasis ? 'text-[26px] leading-[30px]' : 'text-lg'
				)}
			>
				{value}
			</span>
			{sublabel && (
				<span className='truncate text-neutral-500 text-xs'>
					{sublabel}
				</span>
			)}
		</div>
	)
}

interface HostLookupStatusCardProps {
	status: MarqueeResolutionSourceStatus
	/** Never `empty` here: that state replaces the card entirely. */
	state: Exclude<HostLookupTableState, 'empty'>
}

/**
 * What the lookup table currently holds, and where it came from.
 *
 * The provenance half is the part worth reading twice. A `seeded` table has a
 * real row count and null `uploaded_at` / `uploaded_by`, because
 * `GET /resolution-source` reports the uploaded snapshot and the shipped list
 * is nobody's upload. Rendering those nulls through a date formatter would put
 * "Invalid Date" (or worse, "never uploaded") next to several hundred rows that
 * are resolving perfectly well, so that state gets its own single tile that
 * says what the table actually is instead of two tiles reporting a blank.
 *
 * The badge stays "Active" either way: both states resolve. Only an empty table
 * does not, and that one never reaches this card.
 */
export function HostLookupStatusCard({
	status,
	state
}: HostLookupStatusCardProps) {
	const { t } = useTranslation()

	// Whole calendar days, the same measure the lifecycle badge counts down
	// with, so no relative-time plugin is pulled in for one caption.
	const daysAgo = status.uploaded_at
		? differenceInCalendarDays(new Date(), parseDate(status.uploaded_at))
		: 0
	const uploadedAgo =
		daysAgo > 0
			? t('marquee:host_lookup_updated_days_ago', { count: daysAgo })
			: t('marquee:host_lookup_updated_today')

	return (
		<div className='flex flex-col gap-6 rounded-airbnb-2xl border border-grey-200 bg-neutral-white p-6 shadow-airbnb-3'>
			<div className='flex items-start justify-between gap-4'>
				<h2 className='font-medium text-lg text-neutral-900'>
					{t('marquee:host_lookup_current_title')}
				</h2>
				<span className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 font-medium text-green-700 text-xs'>
					<span className='size-1.5 shrink-0 rounded-full bg-green-700' />
					{t('marquee:host_lookup_badge_active')}
				</span>
			</div>

			<div className='grid l:grid-cols-3 m:grid-cols-2 gap-6'>
				<StatusTile
					emphasis
					label={t('marquee:host_lookup_rows_label')}
					sublabel={t('marquee:host_lookup_rows_caption')}
					value={String(status.row_count)}
				/>

				{state === 'uploaded' ? (
					<>
						<StatusTile
							label={t('marquee:host_lookup_updated_label')}
							sublabel={uploadedAgo}
							value={formatDate(status.uploaded_at, 'PPp')}
						/>
						<StatusTile
							label={t('marquee:host_lookup_uploader_label')}
							sublabel={t('marquee:host_lookup_uploader_caption')}
							value={
								status.uploaded_by ??
								t('marquee:host_lookup_uploader_unknown')
							}
						/>
					</>
				) : (
					<StatusTile
						label={t('marquee:host_lookup_origin_label')}
						sublabel={t('marquee:host_lookup_origin_caption')}
						value={t('marquee:host_lookup_origin_shipped')}
					/>
				)}
			</div>
		</div>
	)
}
